import {
  Box,
  CircularProgress,
  Stack,
  SxProps,
  TextField,
  TextFieldProps,
  Typography,
  debounce,
} from '@mui/material';
import {
  CSSProperties,
  InputHTMLAttributes,
  forwardRef,
  memo,
  useCallback,
} from 'react';
import { Controller, UseFormTrigger } from 'react-hook-form';
import { getColor } from '../../../assets/theme';
import { trimLeadingWhiteSpace } from '@utils/format';
import DOMPurify from 'dompurify';

/**
 * Props for the InputFieldsController component.
 * @extends Omit<TextFieldProps, 'ref'>
 */
interface InputFieldProps extends Omit<TextFieldProps, 'ref'> {
  /** Name of the input field */
  name: string;
  /** Control object from react-hook-form */
  control: any;
  /** Label for the input field */
  label: string;
  /** Additional props for the input element */
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  /** Additional props for the Input component */
  InputProps?: Record<string, any>;
  /** Type of the input field */
  type?: string;
  /** Object containing validation errors */
  errors: any;
  /** Custom onChange handler */
  handleOnChange?: any;
  /** Flag to hide error text */
  noTextError?: boolean;
  /** Flag to show loading state */
  loading?: boolean;
  /** Custom styles for the wrapper */
  wrapperStyle?: SxProps;
  outerWrapperStyle?: SxProps;
  /** Flag to show only border error */
  borderErrorOnly?: boolean;
  /** Flag to show text length */
  showTextLength?: boolean;
  /** Custom styles for the input */
  inputStyle?: CSSProperties | { [key: string]: CSSProperties };
  /** Flag to capitalize text */
  capitalizeText?: boolean;
  lowerCaseText?: boolean;
  trigger?: UseFormTrigger<any>;
  validateTrigger?: boolean;
  /** Flag to allow only letters */
  lettersOnly?: boolean;
}

/**
 * InputFieldsController component
 *
 * A customized input field component that integrates with react-hook-form.
 * It provides various customization options and handles error display.
 *
 * @component
 * @param {InputFieldProps} props - The component props
 * @param {React.Ref<HTMLInputElement>} ref - Forwarded ref for the input element
 * @returns {JSX.Element} The rendered InputFieldsController component
 */
const InputFieldsController = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      name,
      label,
      control,
      inputProps,
      InputProps,
      type,
      errors,
      handleOnChange,
      noTextError,
      loading,
      wrapperStyle,
      outerWrapperStyle,
      borderErrorOnly = false,
      showTextLength = false,
      capitalizeText = false,
      lowerCaseText = false,
      inputStyle,
      trigger,
      validateTrigger,
      lettersOnly,
      ...etc
    },
    _ref,
  ) => {
    const { danger } = getColor();

    return (
      <Controller
        name={name}
        control={control}
        render={({ field: { ref, ...field } }) => {
          // Debounced function for side effects
          const debouncedChange = useCallback(
            debounce((newValue: string) => {
              handleOnChange?.(newValue);
            }, 10),
            [handleOnChange],
          );
          return (
            <Box sx={{ width: '100%', ...outerWrapperStyle }}>
              <Box
                sx={{
                  position: 'relative',
                  marginBottom: errors[name] && !noTextError ? 0.5 : 2,
                  ...wrapperStyle,
                }}
              >
                {loading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      right: 0,
                      left: 0,
                      zIndex: 2,
                      opacity: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '4px',
                    }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                )}
                <TextField
                  inputRef={(el) => {
                    ref(el);
                    if (_ref) {
                      if (typeof _ref === 'function') _ref(el);
                      else (_ref as any).current = el;
                    }
                  }}
                  disabled={loading}
                  {...field}
                  onChange={(e) => {
                    let newValue = trimLeadingWhiteSpace(e.target.value);
                    const input = e.target as HTMLInputElement;
                    const cursorPosition = input.selectionStart || 0;
                    const hasCustomInputComponent = Boolean(
                      InputProps?.inputComponent,
                    );

                    if (lettersOnly) {
                      newValue = newValue.replace(/[^\p{L}\s',.]/gu, '');
                    }

                    if (lowerCaseText) newValue = newValue.toLowerCase();
                    if (capitalizeText) {
                      newValue = newValue.replace(/\w\S*/g, (word: string) => {
                        return (
                          word.charAt(0).toUpperCase() +
                          word.slice(1).toLowerCase()
                        );
                      });
                    }

                    field?.onChange(newValue);
                    debouncedChange(newValue);

                    // Restore cursor position for native inputs only.
                    if (!hasCustomInputComponent) {
                      setTimeout(() => {
                        if (typeof input.setSelectionRange === 'function') {
                          input.setSelectionRange(
                            cursorPosition,
                            cursorPosition,
                          );
                        }
                      }, 0);
                    }
                  }}
                  value={field.value}
                  label={
                    label ? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(label),
                        }}
                      />
                    ) : undefined
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    ...inputProps,
                    style: { ...(inputProps?.style || {}), overflow: 'auto' },
                  }}
                  InputProps={InputProps}
                  type={type}
                  size='small'
                  {...etc}
                  sx={{
                    width: '100%',
                    '& .MuiInputBase-root': {
                      border: '0!important',
                      input: {
                        textTransform: capitalizeText
                          ? 'capitalize'
                          : inputProps?.style?.textTransform,
                      },
                    },
                    '& .MuiOutlinedInput-root': {
                      '& > fieldset': { borderColor: errors[name] && danger },
                    },
                    '& .MuiFormLabel-root, .MuiSvgIcon-root, .MuiTypography-root':
                      {
                        color: errors[name] ? danger : 'default',
                      },
                    ...(inputStyle || {}),
                    ...(etc?.sx || {}),
                  }}
                  onBlur={(event) => {
                    etc?.onBlur?.(event);

                    if (trigger && (errors?.[name] || validateTrigger)) {
                      trigger(name);
                    }
                  }}
                  variant={inputProps?.readOnly ? 'standard' : etc?.variant}
                  autoComplete='new-password'
                />
                {inputProps?.maxLength && showTextLength && (
                  <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                    pr='14px'
                    pt='3px'
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Poppins',
                        fontSize: 12,
                        marginBottom: 1,
                        marginLeft: 1,
                        fontWeight: 400,
                        lineHeight: '19.92px',
                        letterSpacing: '0.4px',
                        color: danger,
                        display: noTextError ? 'none' : '',
                      }}
                    >
                      {errors[name] && !borderErrorOnly && (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: errors[name]?.message?.toString() || '',
                          }}
                        />
                      )}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'Roboto',
                        fontSize: '12px',
                        color: '#666',
                      }}
                    >
                      {field?.value?.length || 0}/{inputProps?.maxLength}
                    </Typography>
                  </Stack>
                )}
              </Box>
              {errors[name] &&
                !(inputProps?.maxLength && showTextLength) &&
                !borderErrorOnly && (
                  <Typography
                    sx={{
                      fontFamily: 'Poppins',
                      fontSize: 12,
                      marginBottom: 1,
                      marginLeft: 1,
                      fontWeight: 400,
                      lineHeight: '19.92px',
                      letterSpacing: '0.4px',
                      color: danger,
                      display: noTextError ? 'none' : '',
                    }}
                  >
                    <div
                      dangerouslySetInnerHTML={{
                        __html: errors[name]?.message?.toString() || '',
                      }}
                    />
                  </Typography>
                )}
            </Box>
          );
        }}
      />
    );
  },
);

export default memo(InputFieldsController);
